import { useAuthSession, useBillingSummary, useTranslation } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { BillingPage } from '@/features/billing/components/billing-page';
import { useBillingPermissions } from '@/hooks/use-billing-permissions';
import { useCurrentStore } from '@/hooks/use-current-store';
import { supabase } from '@/lib/supabase';

export function BillingRoute(): React.JSX.Element {
  const { t } = useTranslation();
  const authSession = useAuthSession(supabase);
  const currentStore = useCurrentStore(authSession);
  const permissions = useBillingPermissions(currentStore.storeId);
  const summary = useBillingSummary({ client: supabase, storeId: currentStore.storeId });

  if (currentStore.isLoading || permissions.isLoading || summary.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (!permissions.data?.canManageBilling) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-10 text-start">
        <h1 className="font-display text-sand-950 text-2xl">
          {t('admin.billing.errors.permissionDenied')}
        </h1>
        <p className="text-sand-600 mt-3">{t('admin.billing.permissionDeniedBody')}</p>
      </section>
    );
  }

  if (summary.isError || !summary.data || !currentStore.storeId) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-10 text-start">
        <h1 className="font-display text-sand-950 text-2xl">{t('common.error')}</h1>
        <p className="text-sand-600 mt-3">{t('common.errors.networkFailed')}</p>
        <Button type="button" className="mt-4" onClick={() => void summary.refetch()}>
          {t('common.retry')}
        </Button>
      </section>
    );
  }

  return <BillingPage storeId={currentStore.storeId} summary={summary.data} />;
}
